import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { EphemeralTokenResponse, TokenState } from './ephemeral-token.interface';
import { EPHEMERAL_TOKEN_CONFIG } from './ephemeral-token.config';

/**
 * Servicio para gestionar tokens efímeros de autenticación
 * 
 * CARACTERÍSTICAS:
 * - Token vive SOLO en memoria (no localStorage, sessionStorage ni cookies)
 * - Token de un solo uso
 * - TTL de 3 minutos
 * - Auto-renovación si expira
 * 
 * IMPORTANTE: Este servicio está implementado pero NO activado en el flujo actual.
 * Para usarlo manualmente en un componente:
 * 
 * constructor(private ephemeralToken: EphemeralTokenService) {}
 * 
 * submitForm() {
 *   this.ephemeralToken.getToken().pipe(
 *     switchMap(token => this.http.post(url, data, { headers: { Authorization: `Bearer ${token}` }}))
 *   ).subscribe(...)
 * }
 */
@Injectable({
  providedIn: 'root'
})
export class EphemeralTokenService {
  /**
   * Estado del token en memoria
   * ⚠️ NUNCA persistir en localStorage/sessionStorage/cookies
   */
  private tokenState: TokenState = {
    token: null,
    expiresAt: null
  };

  // ========================================
  // 🧪 MODOS DE TESTING - Comentar/Descomentar según necesites
  // ========================================
  
  /**
   * 🔴 ESCENARIO 1: Enviar request SIN TOKEN (para probar error de autenticación)
   * Descomenta esta línea para activar:
   */
//   private readonly FORCE_NO_TOKEN = true;

  /**
   * 🟡 ESCENARIO 2: Enviar request con TOKEN EXPIRADO (para probar manejo de expiración)
   * Descomenta estas líneas para activar:
   */
//   private readonly FORCE_EXPIRED_TOKEN = true;
//   private readonly EXPIRED_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJCSUxMQ0VOVFJJWC1TRUNVUklUWS1TRVJWSUNFLUJBQ0tFTkQtd2ViZm9ybSIsInR5cGUiOiJ3ZWItZm9ybSIsIm9yaWdpbiI6Imh0dHBzOi8vZGV2LWZvcm11bGFyaW9wYWdvLmJpbGxjZW50cmljLmNvbSIsImNsaWVudF9pcCI6IjE5MC4xNC4yMTEuMTk0IiwidXNlcl9hZ2VudCI6Ii05MzU3Mzk4OTgiLCJwZXJtaXNzaW9ucyI6IlJPTEVfd2ViX2Zvcm0iLCJpYXQiOjE3NjY0MTY5MTYsImV4cCI6MTc2NjQxNzA5NiwianRpIjoiMzZiYTA4Y2EtYjcxNC00MDMxLTg2OTctMzUzNTE2NDYwOTc0IiwibmJmIjoxNzY2NDE2OTE2fQ.ycaXALXa87Km2EvNkLtxFyMTUJ-HEvw4vSuN9G2uCAg';

  /**
   * 🟢 ESCENARIO 3: Funcionamiento NORMAL (producción)
   * Este es el comportamiento por defecto cuando todo está comentado arriba
   */

  constructor(private http: HttpClient) {}

  /**
   * Obtiene un token efímero válido
   * Si el token actual es válido, lo retorna
   * Si no existe o expiró, solicita uno nuevo
   * 
   * @returns Observable con el token
   */
  getToken(): Observable<string> {
    if (this.isTokenValid()) {
      return of(this.tokenState.token!);
    }

    return this.requestNewToken();
  }

  /**
   * Solicita un nuevo token al backend de seguridad
   * 
   * @returns Observable con el token
   */
  private requestNewToken(): Observable<string> {
    const url = `${EPHEMERAL_TOKEN_CONFIG.securityApiUrl}${EPHEMERAL_TOKEN_CONFIG.tokenEndpoint}`;

    return this.http.post<EphemeralTokenResponse>(url, {}).pipe(
      tap(response => {
        this.storeToken(response.token, response.expiresIn);
      }),
      map(response => response.token),
      catchError(error => {
        this.clearToken();
        return throwError(() => error);
      })
    );
  }

  /**
   * Almacena el token en memoria con su tiempo de expiración
   * 
   * @param token Token JWT
   * @param expiresIn Segundos hasta expiración
   */
  private storeToken(token: string, expiresIn: number): void {
    const now = Date.now();
    // Restamos 10 segundos como buffer de seguridad
    const expiresAt = now + ((expiresIn - 10) * 1000);

    this.tokenState = {
      token,
      expiresAt
    };
  }

  /**
   * Verifica si el token actual es válido
   * 
   * @returns true si el token existe y no ha expirado
   */
  isTokenValid(): boolean {
    if (!this.tokenState.token || !this.tokenState.expiresAt) {
      return false;
    }

    const now = Date.now();
    const isValid = now < this.tokenState.expiresAt;

    if (!isValid) {
      this.clearToken();
    }

    return isValid;
  }

  /**
   * Limpia el token de la memoria
   * Se debe llamar después de usar el token (un solo uso)
   */
  clearToken(): void {
    this.tokenState = {
      token: null,
      expiresAt: null
    };
  }

  /**
   * Obtiene el token actual sin validar
   * ⚠️ Uso interno solamente
   * 
   * @returns Token actual o null
   */
  getCurrentToken(): string | null {
    return this.tokenState.token;
  }

  /**
   * Fuerza la renovación del token
   * Útil para testing o debugging
   * 
   * @returns Observable con el nuevo token
   */
  renewToken(): Observable<string> {
    this.clearToken();
    return this.requestNewToken();
  }
}
